import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import type {
	SelectInlineCommentCompoundExperience,
	StartAttributes,
} from '@atlaskit/editor-plugin-annotation';

import type { ExperienceAttributes } from './ExperienceEvent';
import { collectAll } from './ExperienceEventCollector';
import {
	SELECT_INLINE_COMMENT_EXPERIENCE,
	SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
	SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
} from './ExperienceName';
import { getExperienceTracker } from './getExperienceTracker';

let runningExperiences: string[] = [];
let commonSelectInlineCommentExperiencesAttributes: ExperienceAttributes = {};

function getSelectInlineCommentExperienceDebug(experienceName: string) {
	function createInlineCommentExperienceDebug({
		error,
		createAnalyticsEvent,
		extraAttributes,
	}: {
		createAnalyticsEvent: CreateUIAnalyticsEvent;
		error: Error;
		extraAttributes?: ExperienceAttributes;
	}) {
		const experienceTracker = getExperienceTracker();

		const experienceAttributes = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		);
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'error',
				actionSubject: SELECT_INLINE_COMMENT_EXPERIENCE,
				source: experienceName,
				attributes: {
					...experienceAttributes,
					...commonSelectInlineCommentExperiencesAttributes,
					...extraAttributes,

					error: error.message || String(error),
					stack: error.stack,
				},
			},
		}).fire();
	}
	return createInlineCommentExperienceDebug;
}

/**
 * Comment selection can occur in a variety of ways;
 * - with sidebar open
 *   - select a document annotation
 *   - select a comment in the sidebar
 *   - select a subsequent comment in the sidebar (different logic is used between first and subsequent sidebar selections)
 * - with sidebar closed
 *   - select a document annotation
 *   - using the keyboard or ui next/previous navigations
 *   - via loading a page with a comment in the query string
 * - via notifications
 *
 * The experience tracker has a number of constraints;
 *
 * At the start of the experience, the “show” and “select” can be in different orders
 *  - sidebar vs panel vs annotation selection entrypoints
 * To accommodate this – the parent experience does not have an explicit start, and will be started when either the select or show sub experiences start.
 * To avoid restarts – when starting the parent experience – we check if there is an experience running with the same annotation id, and if so do nothing (ie. don’t start a new one).
 *
 * At the start of the experience, the annotationId is not always known
 * - panel next/previous navigation
 * When a start occurs in this scenario, we always restart the experience
 *
 * For some experience the first time we get a reliable signal that an annotation has been selected – is when the selection has completed
 * - annotation based selections and un wired product selections (at time of writing, notifications)
 * To accommodate this – on selection complete – we check if there is an active select experience, and if not start one.
 */
export const selectInlineCommentCompoundExperience: SelectInlineCommentCompoundExperience = {
	/**
	 * Intended for internal use only.
	 *
	 * When the selectAnnotation and selectAnnotation are started they
	 * fire this with the start properties.
	 */
	_start: (startAttributes: StartAttributes) => {
		const experienceTracker = getExperienceTracker();
		const runningExperienceAttributes = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		)?.attributes;

		const shouldSkipDueToRunningExperience = skipDueToRunningExperienceWithSameId({
			startAttributes,
			experienceAttributes: runningExperienceAttributes,
		});
		// If there is an existing experience tracker for the same annotationId
		// we assume the experience is still active, and don't start a new one.
		// Note: for keyboard/comment navigation, we don't get an annotationId on the startAttributes
		// so this will always be false. They set the annotationId in synchronous code following the start,
		// so it should still be set prior to any additional start events.
		if (shouldSkipDueToRunningExperience) {
			// However -- if the second start has a more specific entry point
			// we update the entry point attribute of the existing experience.
			if (
				(runningExperienceAttributes?.entryPoint as string | undefined)?.startsWith('unknown') &&
				!startAttributes.entryPoint.startsWith('unknown')
			) {
				selectInlineCommentCompoundExperience.addCommonAttributes({
					entryPoint: startAttributes.entryPoint,
				});
			}

			return;
		}
		if (
			// When the side bar is open, and the user selects a comment using the keyboard,
			// the keyboard navigation experience is first (when the annotationId is not known),
			// followed by the show comment experience
			startAttributes.entryPoint === 'unknown:CommentsPanelList' &&
			runningExperienceAttributes?.entryPoint === 'keyboard-navigation' &&
			runningExperienceAttributes?.annotationId === undefined
		) {
			selectInlineCommentCompoundExperience.addCommonAttributes({
				annotationId: startAttributes.annotationId,
			});
			return;
		}

		// We reset experience state if we are starting a new experience
		runningExperiences = [SELECT_INLINE_COMMENT_EXPERIENCE];
		commonSelectInlineCommentExperiencesAttributes = {
			pageClass: startAttributes.pageClass,
			commentType: startAttributes.commentType,
			annotationId: 'annotationId' in startAttributes ? startAttributes.annotationId : undefined,
			entryPoint: startAttributes.entryPoint,
		};

		if (startAttributes.linkedCompoundTaskId) {
			selectInlineCommentCompoundExperience.addCommonAttributes({
				linkedCompoundTaskId: startAttributes.linkedCompoundTaskId,
			});
		}

		// THen start a new compound experience
		experienceTracker.start({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			attributes: commonSelectInlineCommentExperiencesAttributes,
			// If the annotation is not selected and shown to the user within 7 seconds -- we assume there has been a failure to select the annotation
			timeout: 7000,

			collect: (events, experience) => {
				collectAll([
					SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
					SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				])(events, experience);
			},
		});

		const compoundTaskId = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		)?.taskId;
		if (compoundTaskId) {
			// this is expected to always be the case, the condition is to keep typescript
			// happy.
			selectInlineCommentCompoundExperience.addCommonAttributes({ compoundTaskId });
		}
	},
	addCommonAttributes: (additionalCommonAttributes: {
		[key: string]: string | number | boolean;
	}) => {
		const experienceTracker = getExperienceTracker();
		commonSelectInlineCommentExperiencesAttributes = {
			...commonSelectInlineCommentExperiencesAttributes,
			...additionalCommonAttributes,
		};
		for (const runningExperience of runningExperiences) {
			experienceTracker.updateAttributes({
				name: runningExperience,
				attributes: additionalCommonAttributes,
			});
		}
	},
	abort: ({ abortReason }: { abortReason: 'test abort' }) => {
		const experienceTracker = getExperienceTracker();
		experienceTracker.abort({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			reason: abortReason,
			checkForTimeout: true,
		});
	},
	selectAnnotation: {
		debug: getSelectInlineCommentExperienceDebug(
			SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
		),
		start: (startAttributes: StartAttributes) => {
			const experienceTracker = getExperienceTracker();
			selectInlineCommentCompoundExperience._start(startAttributes);
			const experienceAttributes = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
			);

			const shouldSkipDueToRunningExperience = skipDueToRunningExperienceWithSameId({
				startAttributes,
				experienceAttributes: experienceAttributes?.attributes,
			});
			// If the experience has already started for this annotation -- we don't start a new one
			// this can happen when the selectAnnotation is called after the showComment
			// (ie. when the user selects a comment in the sidebar)
			if (shouldSkipDueToRunningExperience) {
				return;
			}
			experienceTracker.start({
				name: SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
				timeout: 5000,
			});
			// The select experience can be started after the show experience
			// (ie. when the user selects a comment in the sidebar)
			// In this case we still want to track where the select experience started.
			selectInlineCommentCompoundExperience.addCommonAttributes({
				selectAnnotationEntryPoint: startAttributes.entryPoint,
			});
			runningExperiences.push(SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE);
		},
		abort: ({ abortReason }: { abortReason: 'Comment navigation when only one comment' }) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.abort({
				name: SELECT_INLINE_COMMENT_EXPERIENCE,
				reason: abortReason,
			});
		},
		complete: (startAttributes: StartAttributes) => {
			const experienceTracker = getExperienceTracker();
			// There can be cases where no start has been called for the select experience.
			// - When a product consumer of the editor setselectedannotation api calls the select experience
			//   and has not been instrumented.
			// - When the annotation selection is via editor logic (ie. editor selection being moved onto
			//   an annotation).
			const experienceState = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
			);
			if (
				experienceState === null ||
				// When the experience state has stopped -- we assume it's from a previous experience
				experienceState.hasStopped === true
			) {
				selectInlineCommentCompoundExperience.selectAnnotation.start(startAttributes);
			}
			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
			});
		},
	},
	showComment: {
		debug: getSelectInlineCommentExperienceDebug(SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE),
		debugPoint: (message: string, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.attachDebugPoint({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				message,
				attributes,
			});
		},
		// The logic around starting the compound experience is opinionated about the showComment
		// experience always starting with an annotationId.
		// This is because it will prevent experience restarts where the showComment experience
		// is called multiple times with the same annotationId.
		start: (startAttributes: StartAttributes & { annotationId: string }) => {
			const experienceTracker = getExperienceTracker();
			selectInlineCommentCompoundExperience._start(startAttributes);

			const compoundExperienceState = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
			);

			const shouldSkipDueToRunningExperience = skipDueToRunningExperienceWithSameId({
				startAttributes,
				experienceAttributes: compoundExperienceState?.attributes,
			});
			// If the experience has already started for this annotation -- we don't start a new one
			// this can happen when the showComment is called after the selectComment
			// (ie. when the user selects an annotation in the document)
			if (shouldSkipDueToRunningExperience) {
				return;
			}

			experienceTracker.start({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
				timeout: 5000,
			});
			// The show experience can be started after the select experience
			// (ie. when the user selects an annotation in the document)
			// In this case we still want to track where the show experience started.
			selectInlineCommentCompoundExperience.addCommonAttributes({
				showCommentEntryPoint: startAttributes.entryPoint,
			});
			runningExperiences.push(SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE);
		},
		abort: ({
			abortReason,
		}: {
			abortReason: 'Non inline comment being shown' | 'Comment sidebar dismissed';
		}) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.abort({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				reason: abortReason,
			});
		},
		fail: (error: Error, extraAttributes?: { misalignedBy: number }) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.fail({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				error,
				attributes: { ...commonSelectInlineCommentExperiencesAttributes, ...extraAttributes },
			});
		},
		complete: () => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
			});
		},
	},
};

function skipDueToRunningExperienceWithSameId({
	startAttributes,
	experienceAttributes,
}: {
	startAttributes: StartAttributes;
	experienceAttributes: ExperienceAttributes | undefined;
}) {
	const hasAnnotationId = 'annotationId' in startAttributes;

	// For keyboard navigation and comment navigation, we don't know the annotationId when the experience starts.
	// These entry points should not be hit multiple times for the same selection so we don't need to skip here.
	if (!hasAnnotationId) {
		return false;
	}

	// If there is no running experience - we don't want to skip
	if (experienceAttributes === undefined) {
		return false;
	}

	// If the running experience has the same annotationId as the newly triggered one -- we want to skip.
	if (experienceAttributes?.annotationId === startAttributes.annotationId) {
		return true;
	}

	// In all other cases -- we don't want to skip
	return false;
}
